import { env } from "@/lib/environment";

export interface GeoLocationApiResponse {
  isFallback?: boolean;
  ip?: string;
  port?: string;
  geo?: {
    countryRegionName: string;
    city: string;
    countryCode: string;
    timezone: string;
    postalCode: string;
    countryRegion: string;
    location: {
      latitude: string;
      longitude: string;
    };
    countryName: string;
  };
  request?: {
    httpVersion: string;
  };
  error?: string;
}

interface GeoLocationFetchOptions {
  /** Explicit client IP to forward to the upstream API */
  ipAddress?: string | null;
  /** Additional fetch options (headers merged below) */
  fetchOptions?: globalThis.RequestInit;
}

/**
 * Core geolocation API function that can be used on both client and server
 * This is the single source of truth for the geolocation API call
 */
export async function fetchGeoLocationData({
  ipAddress,
  fetchOptions,
}: GeoLocationFetchOptions = {}): Promise<GeoLocationApiResponse> {
  const maxAttempts = 4; // total attempts including the first one
  const baseDelayMs = 300; // initial backoff delay

  const sleep = (ms: number) => new Promise(res => setTimeout(res, ms));

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const requestHeaders = new Headers(fetchOptions?.headers ?? undefined);

      if (ipAddress) {
        requestHeaders.set("x-forwarded-for", ipAddress);
      }

      // Avoid setting forbidden headers in the browser while keeping the
      // server-side identification header for observability when available.
      if (typeof window === "undefined" && !requestHeaders.has("User-Agent")) {
        requestHeaders.set("User-Agent", "Pickcel-Website/1.0");
      }

      const response = await fetch(`${env.NEXT_PUBLIC_THINKSTACK_API_URL}/api/ip/detect`, {
        ...fetchOptions,
        cache: fetchOptions?.cache ?? "no-store",
        headers: requestHeaders,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      const isLast = attempt === maxAttempts;
      console.error(`Geolocation API error (attempt ${attempt}/${maxAttempts}):`, error);
      if (isLast) {
        return {
          error: error instanceof Error ? error.message : "Failed to fetch geolocation data",
        };
      }
      // Exponential backoff with jitter
      const jitter = Math.random() * 150;
      const delay = Math.min(3000, baseDelayMs * Math.pow(2, attempt - 1)) + jitter;
      await sleep(delay);
    }
  }

  // default set as us/california
  return {
    isFallback: true,
    geo: {
      countryRegionName: "California",
      city: "Los Angeles",
      countryCode: "US",
      timezone: "America/Los_Angeles",
      postalCode: "90060",
      countryRegion: "CA",
      location: {
        latitude: "34.05440",
        longitude: "-118.24400",
      },
      countryName: "United States",
    },
    request: {
      httpVersion: "2.0",
    },
  };
}

/**
 * Transform API response to a simplified format for client-side use
 */
export function transformGeoLocationData(apiData: GeoLocationApiResponse) {
  if (apiData.error || !apiData.geo) {
    return null;
  }

  return {
    countryCode: apiData.geo.countryCode,
    countryName: apiData.geo.countryName,
    city: apiData.geo.city,
    region: apiData.geo.countryRegionName,
    timezone: apiData.geo.timezone,
    postalCode: apiData.geo.postalCode,
    latitude: apiData.geo.location.latitude,
    longitude: apiData.geo.location.longitude,
    ip: apiData.ip,
  };
}
